import { Typography, TypographyProps } from '@mui/material';
import DOMPurify from 'dompurify';
import he from 'he';
import hljs from 'highlight.js';
import { useEffect, useMemo } from 'react';

const ESCAPE_TAGS = ['script', 'iframe', 'object', 'embed', 'svg', 'math'];

const escapeTagToText = (input: string, tagName: string) =>
  input.replace(new RegExp(`<\\s*\\/?\\s*${tagName}\\b[^>]*>`, 'gi'), (m) =>
    m.replace(/</g, '&lt;').replace(/>/g, '&gt;'),
  );

const ATTRIBUTES_TO_CLEAR = ['class', 'width', 'height', 'style'];

const escapeHtmlCharsInCodeBlock = (value: string): string => {
  const regex = /<code[^>]*?>([\s\S]*?)<\/code>/gi;

  const resultValue = value.replace(regex, (match, content) => {
    const decodedContent = he.decode(content);
    const encodedContent = he.encode(decodedContent);
    return match.replace(content, encodedContent);
  });

  return resultValue;
};

interface MarkdownFormatterProps extends Omit<TypographyProps<'div'>, 'component'> {
  text: string;
  attributesToClear?: string[];
}

const TextFormatter = ({
  text,
  attributesToClear = ATTRIBUTES_TO_CLEAR,
  ...props
}: MarkdownFormatterProps) => {
  const { sx, ...restProps } = props;
  const normalizedText = escapeHtmlCharsInCodeBlock(
    attributesToClear.reduce(
      (acc, attr) => acc.replaceAll(new RegExp(`\\s*${attr}="[^"]*"`, 'g'), ''),
      text.replace(/\\n/g, '\n'),
    ),
  );
  const normalizedTextWithEscapedScript = ESCAPE_TAGS.reduce(
    (acc, tag) => escapeTagToText(acc, tag),
    normalizedText,
  );

  const sanitizedHtml = useMemo(
    () =>
      DOMPurify.sanitize(normalizedTextWithEscapedScript, {
        ALLOWED_TAGS: [
          'p',
          'br',
          'strong',
          'em',
          'u',
          's',
          'ul',
          'ol',
          'li',
          'a',
          'img',
          'pre',
          'code',
          'blockquote',
          'h1',
          'h2',
          'h3',
          'h4',
          'h5',
          'h6',
          'span',
          'div',
          'video',
          'audio',
        ],
        ALLOWED_ATTR: [
          'href',
          'src',
          'alt',
          'title',
          'target',
          'rel',
          'class',
          'style',
          'width',
          'height',
          'loading',
          'controls',
        ],
        FORBID_TAGS: ESCAPE_TAGS,
      }),
    [normalizedTextWithEscapedScript],
  );

  useEffect(() => {
    hljs.highlightAll();
  }, []);

  return (
    <Typography
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...restProps}
      component='div'
      sx={{
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        '& pre, & pre code': {
          maxWidth: '100%',
          whiteSpace: 'pre-wrap !important',
          overflowWrap: 'anywhere',
        },
        '& img, & video': { height: 'auto' },
        '& img, & video, & audio': { maxWidth: '100%', display: 'block' },
        '& img': { margin: '1em auto' },
        '& video': { padding: '1em' },
        '& audio': { margin: '1em' },
        ...sx,
      }}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};

export default TextFormatter;
